import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { FulfillerModalConfig, useFulfiller } from './FulfillerContext';
import { useEffect, useState } from 'react';

import { retrieveFulfillerList } from './utilities/fulfillerHelper';
import { FulfillerItem, SelectedFulfillerConfig } from './models/HeaderTypes';
import NotFoundState from './components/common/NotFoundState';

const FulfillerLayout = () => {
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenantId');
  const { fulfillerData, setFulfillerDetails } = useFulfiller();
  const navigate = useNavigate();
  // State to store fulfiller data
  const [fulfillerList, setFulfillerList] = useState<FulfillerItem[]>([]);
  const [isFulfillerListLoading, setIsFulfillerListLoading] = useState(true);
  const [hasInvalidTenantId, setHasInvalidTenantId] = useState(false);

  useEffect(() => {
    // Fetches the fulfiller data asynchronously and updates state
    const fetchStateFulfillerData = async () => {
      setIsFulfillerListLoading(true);
      try {
        const fulfillerObj = await retrieveFulfillerList();
        setFulfillerList(fulfillerObj);
      } catch (error) {
        console.error('Error fetching fulfiller list:', error);
        setFulfillerList([]);
      } finally {
        setIsFulfillerListLoading(false);
      }
    };
    fetchStateFulfillerData();
  }, []);

  useEffect(() => {
    if (!tenant && fulfillerData?.fulfillerLocation?.value) {
      navigate(
        `/entitytypes?tenantId=${fulfillerData.fulfillerLocation.value}`,
        { replace: true },
      );
    }
  }, [tenant, fulfillerData, navigate]);

  useEffect(() => {
    if (!tenant || isFulfillerListLoading) {
      setHasInvalidTenantId(false);
      return;
    }

    const newSelectedFulfiller = fulfillerList.find((f) =>
      f.fulfillerLocations.find((l) => l.value === tenant),
    );

    if (newSelectedFulfiller) {
      const selectedFulfillerLocation =
        newSelectedFulfiller.fulfillerLocations.find((l) => l.value === tenant);

      setFulfillerDetails({
        fulfillerData: {
          fulfiller: newSelectedFulfiller.fulfiller,
          fulfillerLocation: selectedFulfillerLocation,
          xCometAccountId: newSelectedFulfiller.xCometAccountId,
        } as SelectedFulfillerConfig,
        xCometAccountId: newSelectedFulfiller.xCometAccountId,
      } as FulfillerModalConfig);
      setHasInvalidTenantId(false);
      return;
    }

    setFulfillerDetails(null);
    setHasInvalidTenantId(true);
  }, [tenant, fulfillerList, isFulfillerListLoading, setFulfillerDetails]);

  if (hasInvalidTenantId) {
    const fallbackPath = fulfillerData?.fulfillerLocation?.value
      ? `/entitytypes?tenantId=${fulfillerData.fulfillerLocation.value}`
      : '/';

    return (
      <NotFoundState
        message={`Unable to find tenant with ID ${tenant}.`}
        navigationLink={{ to: fallbackPath, text: 'Entity Types' }}
      />
    );
  }

  return <Outlet />;
};

export default FulfillerLayout;
